import { DEFAULT_RESTAURANT_TABLE_THEME } from '../../../types/restaurant/RestaurantTableTypes';
import {
  AppearanceSettingsForm,
  GeneralSettingsForm,
  InventorySettingsForm,
  KitchenSettingsForm,
  PosSettingsForm,
  PrintingSettingsForm,
  ReservationSettingsForm,
  RestaurantAppearanceSettings,
  RestaurantGeneralSettings,
  RestaurantInventorySettings,
  RestaurantKitchenSettings,
  RestaurantPosSettings,
  RestaurantPrintingSettings,
  RestaurantReservationSettings,
} from './RestaurantSettingsTypes';

const color = (value: string | null | undefined, fallback: string) => {
  const trimmed = (value ?? '').trim();
  return /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(trimmed) ? trimmed : fallback;
};

const bool = (value: boolean | null | undefined) => value === true;

export const mapGeneralSettings = (
  dto: RestaurantGeneralSettings
): GeneralSettingsForm => ({
  active: bool(dto.active),
  enabled: bool(dto.enabled),
  multipleWaitersPerOrder: bool(dto.multipleWaitersPerOrder),
  automaticCloseOrder: bool(dto.automaticCloseOrder),
  defaultPreparationMinutes: dto.defaultPreparationMinutes ?? 1,
});

export const mapPosSettings = (dto: RestaurantPosSettings): PosSettingsForm => ({
  active: bool(dto.active),
  splitBillEnabled: bool(dto.splitBillEnabled),
  serviceChargeEnabled: bool(dto.serviceChargeEnabled),
  serviceChargePercentage: Number(dto.serviceChargePercentage ?? 0),
  allowReopenOrder: bool(dto.allowReopenOrder),
  allowTableTransfer: bool(dto.allowTableTransfer),
  allowMergeOrders: bool(dto.allowMergeOrders),
  allowMoveOrderItems: bool(dto.allowMoveOrderItems),
});

export const mapKitchenSettings = (
  dto: RestaurantKitchenSettings
): KitchenSettingsForm => ({
  active: bool(dto.active),
  kitchenWorkflowEnabled: dto.kitchenWorkflowEnabled !== false,
  kitchenDisplayEnabled: bool(dto.kitchenDisplayEnabled),
  kitchenPrinterEnabled: bool(dto.kitchenPrinterEnabled),
  preparationTimerEnabled: bool(dto.preparationTimerEnabled),
  orderCompletionMode: dto.orderCompletionMode ?? 'MANUAL',
  autoAdvanceItemsByTime: bool(dto.autoAdvanceItemsByTime),
  maxOrdersInPreparation: dto.maxOrdersInPreparation ?? 0,
  allowStationTransfer: bool(dto.allowStationTransfer),
  parallelPreparationEnabled: bool(dto.parallelPreparationEnabled),
});

export const mapInventorySettings = (
  dto: RestaurantInventorySettings
): InventorySettingsForm => ({
  active: bool(dto.active),
  inventoryConsumptionMoment:
    dto.inventoryConsumptionMoment ?? 'WHEN_PREPARING',
  inventoryConsumptionEnabled: bool(dto.inventoryConsumptionEnabled),
  allowNegativeStock: bool(dto.allowNegativeStock),
  validateStockBeforePreparation: bool(dto.validateStockBeforePreparation),
});

export const mapReservationSettings = (
  dto: RestaurantReservationSettings
): ReservationSettingsForm => ({
  active: bool(dto.active),
  reservationsEnabled: bool(dto.reservationsEnabled),
  reservationToleranceMinutes: dto.reservationToleranceMinutes ?? 0,
  maximumAdvanceDays: dto.maximumAdvanceDays ?? 0,
  automaticCancellationEnabled: bool(dto.automaticCancellationEnabled),
  allowGuestReservations: bool(dto.allowGuestReservations),
  allowMultipleTables: bool(dto.allowMultipleTables),
  allowOverbooking: bool(dto.allowOverbooking),
  sendReservationReminders: bool(dto.sendReservationReminders),
});

export const mapPrintingSettings = (
  dto: RestaurantPrintingSettings
): PrintingSettingsForm => ({
  active: bool(dto.active),
  printKitchenTicket: bool(dto.printKitchenTicket),
  printCustomerTicket: bool(dto.printCustomerTicket),
  printInvoiceAfterPayment: bool(dto.printInvoiceAfterPayment),
  printOrderNumber: bool(dto.printOrderNumber),
  printCashierCopy: bool(dto.printCashierCopy),
  printCustomerCopy: bool(dto.printCustomerCopy),
});

export const mapAppearanceSettings = (
  dto: RestaurantAppearanceSettings
): AppearanceSettingsForm => ({
  active: bool(dto.active),
  tableFillColor: color(
    dto.tableFillColor,
    DEFAULT_RESTAURANT_TABLE_THEME.tableFillColor
  ),
  tableLineColor: color(
    dto.tableLineColor,
    DEFAULT_RESTAURANT_TABLE_THEME.tableLineColor
  ),
  chairFillColor: color(
    dto.chairFillColor,
    DEFAULT_RESTAURANT_TABLE_THEME.chairFillColor
  ),
  chairLineColor: color(
    dto.chairLineColor,
    DEFAULT_RESTAURANT_TABLE_THEME.chairLineColor
  ),
});

export const toGeneralUpdateDTO = (form: GeneralSettingsForm) => ({ ...form });
export const toPosUpdateDTO = (form: PosSettingsForm) => ({ ...form });
export const toKitchenUpdateDTO = (form: KitchenSettingsForm) => ({ ...form });
export const toInventoryUpdateDTO = (form: InventorySettingsForm) => ({ ...form });
export const toReservationUpdateDTO = (form: ReservationSettingsForm) => ({ ...form });
export const toPrintingUpdateDTO = (form: PrintingSettingsForm) => ({ ...form });
export const toAppearanceUpdateDTO = (form: AppearanceSettingsForm) => ({
  ...form,
});

